import { BACKEND_ROOT, FRONTEND_ROOT } from '../../config/host-config';
import { History } from 'history';
import { LoginResponse } from '../../enums';

export class Auth {
    public static MfaReceiptStorageKey = 'mfa_authorization_receipt';
    public static Claims = {
        Role: 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
        TeamsManaging: 'http://ponderaadvisors.com/claims/managedTeams',
    };
    public static AccessToken = 'access_token';
    private static busy : boolean = false;

    private history: History;

    constructor(history?: any) {
        if (!!history) {
            this.history = history;
        }

        // binds functions to keep this context
        this.logout = this.logout.bind(this);
        this.isAuthenticated = this.isAuthenticated.bind(this);
    }

    currentRole = (): any => {
        const token = this.decodeJwt(localStorage.getItem(Auth.AccessToken) || '');
        const role = token[Auth.Claims.Role].toLowerCase();
        return role;
    }

    managedTeams = (): any => {
        const token = this.decodeJwt(localStorage.getItem(Auth.AccessToken) || '');
        const managedTeams = token[Auth.Claims.TeamsManaging];
        const splitTeams = managedTeams.split(',');
        return splitTeams;
    }

    decodeJwt = (jwt: string): any => {
        try {
            return JSON.parse(window.atob(jwt.split('.')[1]));
        } catch(e) {
            console.log(`Failed to decode JWT "${jwt}. Error: ${e}"`);
        }
    }

    bypassMfa = async (userId: number) => {
        const endpoint = `${BACKEND_ROOT}/api/authentication/bypassMfaRequest/${userId}`;

        const request: RequestInit = {
            method: 'POST',
            mode: 'cors',
            cache: 'default',
            body: JSON.stringify({ userId }),
            headers: this.getAuthHeaders(),
        };

        await fetch(endpoint, request);
    }

    resetPassword = async (email: string) => {
        const endpoint = `${BACKEND_ROOT}/api/authentication/passwordResetRequest`;

        const request: RequestInit = {
            method: 'POST',
            mode: 'cors',
            cache: 'default',
            body: JSON.stringify({ email }),
            headers: new Headers({'content-type': 'application/json'}),
        };

        await fetch(endpoint, request);
    }

    setPassword = async (email: string, token: string, password: string): Promise<boolean> => {
        try {
            const endpoint = `${BACKEND_ROOT}/api/authentication/passwordChangeRequest`;

            const request: RequestInit = {
                method: 'POST',
                mode: 'cors',
                cache: 'default',
                body: JSON.stringify({email, token, password}),
                headers: new Headers({'content-type': 'application/json'}),
            };

            const response = await fetch(endpoint, request);
            return response.ok;
        } catch(e) {
            console.log(e);
            return false;
        }
    }

    authenticateWithReceiptAndCode = async (email: string, receipt: string, code: string): Promise<LoginResponse> => {
        const endpoint = `${BACKEND_ROOT}/api/authentication/tokenRequest`;

        const request: RequestInit = {
            method: 'POST',
            mode: 'cors',
            cache: 'default',
            body: JSON.stringify({ MfaAuthorizationReceipt: receipt, MfaCode: code, email }),
            headers: new Headers({'content-type': 'application/json'}),
        };

        let response = await fetch(endpoint, request);
        return await this.handleAuthentication(email, response);
    }

    authenticateWithEmailAndPassword = async (email: string, password: string): Promise<LoginResponse> => {
        const endpoint = `${BACKEND_ROOT}/api/authentication/tokenRequest`;

        const request: RequestInit = {
            method: 'POST',
            mode: 'cors',
            cache: 'default',
            body: JSON.stringify({ email, password }),
            headers: new Headers({'content-type': 'application/json'}),
        };

        let responsePromise : Promise<Response> = new Promise<Response>(() => { return; });

        try {
            if (!Auth.busy) {
                Auth.busy = true;
                responsePromise = fetch(endpoint, request);
            }
        }
        finally {
            Auth.busy = false;
        }
        return await this.handleAuthentication(email, await responsePromise);
    }

    handleAuthentication = async (email: string, response: Response): Promise<LoginResponse> => {
        if (response.status === 403) {
            return LoginResponse.Locked;
        }
        if (response.ok) {
            let responseText = await response.text();
            try {
                let jsonResult = JSON.parse(responseText);
                if (typeof jsonResult !== typeof '') {
                    // If this is an object instead of a string, we may have an MFA challenge.
                    if (jsonResult.mfaNeeded) {
                        localStorage.setItem(Auth.MfaReceiptStorageKey, jsonResult.mfaAuthorizationReceipt);
                        this.history.replace(`/?mode=mfa&email=${encodeURIComponent(email)}`);
                        return LoginResponse.MfaCodeRequired;
                    } else {
                        throw new Error('Failed to retrieve authorization token');
                    }
                }
            } catch(e) {
                // This isn't JSON!
            }

            this.setSession(responseText);

            const redirectPath = this.getRedirectPath();
            if (redirectPath) {
                this.clearRedirectPath();
                this.history.replace(redirectPath);
            } else {
                this.history.replace('/home');
            }

            return LoginResponse.Success;
        }

        // this.history.replace('/');
        return LoginResponse.Incorrect;
    }

    getUserInfo() {
        const endpoint = `${BACKEND_ROOT}/api/user/current`;

        let requestInit: RequestInit = {
            method: 'GET',
            mode: 'cors',
            cache: 'default',
            headers: this.getAuthHeaders()
        };

        return fetch(endpoint, requestInit).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    setSession(jwt: string) {
        console.log('set session');
        // Set the time that the access token will expire at
        const token = this.decodeJwt(jwt);

        const expiresAt = Number(token.exp) * 1000;

        localStorage.setItem('access_token', jwt);
        localStorage.setItem('expires_at', expiresAt.toString());
    }

    impersonateUser(id: number) {
        const endpoint = `${BACKEND_ROOT}/api/authentication/impersonate/${id}`;

        const request: RequestInit = {
            method: 'POST',
            mode: 'cors',
            cache: 'default',
            headers: this.getAuthHeaders(),
        };

        return fetch(endpoint, request).then(
            async (response) => {
                const auth = new Auth();
                auth.setSession(await response.text());
                window.location.href = `${FRONTEND_ROOT}/home`;
            },
            () => {
                throw `Error impersonating user. Please try again.`;
            }
        );
    }

    stopImpersonating() {
        const endpoint = `${BACKEND_ROOT}/api/authentication/stopImpersonate`;

        const request: RequestInit = {
            method: 'POST',
            mode: 'cors',
            cache: 'default',
            headers: this.getAuthHeaders(),
        };

        return fetch(endpoint, request).then(
            async (response) => {
                const auth = new Auth();
                auth.setSession(await response.text());
                window.location.href = `${FRONTEND_ROOT}/home`;
            },
            () => {
                throw `Error stopping impersonation. Please try again.`;
            }
        );
    }

    logout(history: any) {
        // on a user initiated logout, we clear everything from local storage
        localStorage.clear();
        // navigate to the home route
        history.replace('/');
    }

    isAuthenticated() {
        // Check whether the current time is past the 
        // access token's expiry time
        const expiresAt = Number(localStorage.getItem('expires_at'));

        if (expiresAt != null && !isNaN(expiresAt)) {
            return new Date().getTime() < expiresAt;
        }

        return false;
    }

    getAuthHeaders() {
        try {
            if (!this.isAuthenticated()) {
                throw new Error('The user is not authenticated!');
            }

            const accessToken = localStorage.getItem('access_token');
            if (accessToken == null) {
                throw new Error('No access token found in local storage!');
            }

            return new Headers({
                'Authorization': `Bearer ${accessToken}`
            });
        } catch(e) {
            console.log(e);
            // this is a hard short circuit of the app. If a user tries to make an authenticated request 
            // and they are not authenticated, we push them out to the root path of the app, which is the login page.
            // We will log the page they were trying to access so that we can redirect them after authentication.
            this.saveRedirectPath();
            window.location.href = FRONTEND_ROOT;
        }

        return undefined;
    }

    saveRedirectPath() {
        localStorage.setItem('redirect_path', window.location.pathname);
    }

    getRedirectPath() {
        return localStorage.getItem('redirect_path');
    }

    clearRedirectPath() {
        localStorage.removeItem('redirect_path');
    }

    getMfaAuthenticationReceipt() {
        return localStorage.getItem(Auth.MfaReceiptStorageKey) || '';
    }
}



// WEBPACK FOOTER //
// ./src/services/auth/Auth.tsx